import { useQuery } from "@tanstack/react-query";
import { useTenant } from "./useTenant";
import { supabase } from "@/integrations/supabase/client";

interface FamilyAum {
  family_id: string;
  family_name: string;
  aum: number;
  position_count: number;
  weighted_twr_ytd?: number | null;
}

interface FirmMetrics {
  id: string;
  org_id: string;
  period_end: string;
  total_aum_base: number;
  family_count: number;
  active_position_count: number;
  weighted_twr_mtd: number | null;
  weighted_twr_qtd: number | null;
  weighted_twr_ytd: number | null;
  weighted_irr_itd: number | null;
  aum_change_mtd: number | null;
  aum_change_qtd: number | null;
  aum_change_ytd: number | null;
  aum_by_family: FamilyAum[];
  documents_pending: number;
  stale_nav_count: number;
  open_actions_count: number;
  created_at: string;
  updated_at: string;
}

interface FamilyExposure {
  family_id: string;
  family_name: string;
  commitment: number;
  nav: number;
  paid_in: number;
  position_id: string;
}

interface CrossFamilyExposure {
  id: string;
  org_id: string;
  instrument_id: string;
  period_end: string;
  fund_name: string;
  gp_name: string | null;
  strategy: string | null;
  vintage_year: number | null;
  total_commitment_base: number;
  total_nav_base: number;
  total_paid_in_base: number;
  family_count: number;
  family_exposures: FamilyExposure[];
}

interface DealSharing {
  id: string;
  org_id: string;
  source_family_id: string;
  target_family_id: string;
  instrument_id: string | null;
  deal_name: string;
  deal_description: string | null;
  status: 'proposed' | 'interested' | 'declined' | 'invested';
  proposed_at: string;
  proposed_by: string | null;
  responded_at: string | null;
  responded_by: string | null;
  response_notes: string | null;
}

/**
 * Hook for fetching firm-level metrics for MFO organizations
 * Only returns data when the user is in an MFO organization
 */
export function useFirmMetrics() {
  const { orgId, isMFO, isLoading: tenantLoading } = useTenant();

  // Fetch firm metrics (materialized aggregates)
  const {
    data: firmMetrics,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics
  } = useQuery<FirmMetrics | null>({
    queryKey: ['firm-metrics', orgId],
    queryFn: async () => {
      if (!orgId) return null;

      const { data, error } = await supabase
        .from('firm_metrics')
        .select('*')
        .eq('org_id', orgId)
        .order('period_end', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as FirmMetrics | null;
    },
    enabled: !!orgId && isMFO,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch cross-family exposure data
  const {
    data: crossExposure,
    isLoading: exposureLoading,
    error: exposureError,
    refetch: refetchExposure
  } = useQuery<CrossFamilyExposure[]>({
    queryKey: ['cross-family-exposure', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('cross_family_exposure')
        .select('*')
        .eq('org_id', orgId)
        .order('total_commitment_base', { ascending: false });

      if (error) throw error;
      return (data || []) as CrossFamilyExposure[];
    },
    enabled: !!orgId && isMFO,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch deal sharing proposals
  const {
    data: dealSharing,
    isLoading: dealSharingLoading,
    refetch: refetchDealSharing
  } = useQuery<DealSharing[]>({
    queryKey: ['deal-sharing', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('deal_sharing')
        .select('*')
        .eq('org_id', orgId)
        .order('proposed_at', { ascending: false });

      if (error) throw error;
      return (data || []) as DealSharing[];
    },
    enabled: !!orgId && isMFO,
    staleTime: 5 * 60 * 1000,
  });

  // Calculate derived metrics
  const topGPs = crossExposure
    ?.reduce((acc, exp) => {
      const gp = exp.gp_name || 'Unknown GP';
      if (!acc[gp]) {
        acc[gp] = { name: gp, totalCommitment: 0, fundCount: 0, familyCount: new Set<string>() };
      }
      acc[gp].totalCommitment += exp.total_commitment_base || 0;
      acc[gp].fundCount += 1;
      exp.family_exposures?.forEach(fe => acc[gp].familyCount.add(fe.family_id));
      return acc;
    }, {} as Record<string, { name: string; totalCommitment: number; fundCount: number; familyCount: Set<string> }>);

  const topGPsList = Object.values(topGPs || {})
    .map(gp => ({ ...gp, familyCount: gp.familyCount.size }))
    .sort((a, b) => b.totalCommitment - a.totalCommitment)
    .slice(0, 10);

  // Funds with multiple families invested (deal sharing opportunities)
  const sharedFunds = crossExposure?.filter(exp => exp.family_count >= 2) || [];

  const isLoading = tenantLoading || metricsLoading || exposureLoading;
  const error = metricsError || exposureError;

  const refetch = async () => {
    await Promise.all([
      refetchMetrics(),
      refetchExposure(),
      refetchDealSharing(),
    ]);
  };

  return {
    // Core metrics
    firmMetrics,
    crossExposure,
    dealSharing,

    // Derived data
    topGPs: topGPsList,
    sharedFunds,

    // Loading states
    isLoading,
    error,

    // Only valid for MFO
    isMFO,

    // Actions
    refetch,
  };
}

/**
 * Hook for managing firm copilot threads
 */
export function useFirmCopilotThreads() {
  const { orgId, isMFO } = useTenant();

  const { data: threads, isLoading, refetch } = useQuery({
    queryKey: ['firm-copilot-threads', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('firm_copilot_threads')
        .select('*')
        .eq('org_id', orgId)
        .order('last_message_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId && isMFO,
  });

  return { threads, isLoading, refetch };
}

/**
 * Hook for managing firm agents
 */
export function useFirmAgents() {
  const { orgId, isMFO } = useTenant();

  const { data: agents, isLoading, refetch } = useQuery({
    queryKey: ['firm-agents', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('firm_agents')
        .select('*')
        .eq('org_id', orgId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId && isMFO,
  });

  const { data: recentRuns } = useQuery({
    queryKey: ['firm-agent-runs', orgId],
    queryFn: async () => {
      if (!orgId) return [];

      const { data, error } = await supabase
        .from('firm_agent_runs')
        .select('*')
        .eq('org_id', orgId)
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId && isMFO,
  });

  return { agents, recentRuns, isLoading, refetch };
}
